"use client";

import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { MoveHorizontal, ArrowRight, Scale } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const transformations = [{
  id: 1,
  name: "Tesla Model S",
  title: "The Ultimate Mirror Finish",
  stats: "99% GLOSS",
  subStats: "9H CERAMIC",
  quote: "The depth of the black paint is now like a mirror. Ceeday's attention to detail is truly obsessive. It looks better than showroom quality.",
  beforeImg: "https://ceedaydetailing.com/wp-content/uploads/2026/04/Before-wide.png",
  afterImg: "https://ceedaydetailing.com/wp-content/uploads/2026/04/After-wide.png",
  duration: "Full Detail"
}, {
  id: 2,
  name: "Porsche Macan",
  title: "Interior Restoration",
  stats: "DEEP CLEAN",
  subStats: "LEATHER REVIVAL",
  quote: "Years of family trips and pet hair vanished in a single afternoon. The interior smells and feels like the day I bought it.",
  beforeImg: "https://ceedaydetailing.com/wp-content/uploads/2026/04/IMG_1772.jpg",
  afterImg: "https://ceedaydetailing.com/wp-content/uploads/2026/04/IMG_1771-3-scaled.jpg",
  duration: "Interior Detail"
}, {
  id: 3,
  name: "Porsche Macan",
  title: "Precision Paint Correction",
  stats: "SWIRL FREE",
  subStats: "LIQUID REFLECTION",
  quote: "I was worried about the micro-scratches from automated washes. Ceeday completely restored the clarity and protected it for the long haul.",
  beforeImg: "https://ceedaydetailing.com/wp-content/uploads/2026/04/IMG_1766-scaled.jpg",
  afterImg: "https://ceedaydetailing.com/wp-content/uploads/2026/04/IMG_1768-scaled.jpg",
  duration: "Exterior Detail"
}];
const SliderDisplay = React.memo(({
  activeItem,
  sliderPosition,
  isDragging,
  containerRef,
  handleMouseDown,
  handleMouseMove,
  handleTouchMove,
  handleMove,
  onImageLoad,
  loadedCount
}: {
  activeItem: typeof transformations[0];
  sliderPosition: number;
  isDragging: boolean;
  containerRef: React.RefObject<HTMLDivElement | null>;
  handleMouseDown: () => void;
  handleMouseMove: (e: React.MouseEvent) => void;
  handleTouchMove: (e: React.TouchEvent) => void;
  handleMove: (clientX: number) => void;
  onImageLoad: () => void;
  loadedCount: number;
}) => {
  return <div ref={containerRef} className="relative w-full aspect-[16/10] md:aspect-[16/9] rounded-2xl overflow-hidden shadow-2xl border border-primary/20 select-none group cursor-ew-resize" onMouseDown={handleMouseDown} onMouseMove={handleMouseMove} onTouchMove={handleTouchMove} onClick={e => handleMove(e.clientX)}>
      {}
      <div className="absolute inset-0 w-full h-full">
        <img src={activeItem.afterImg} srcSet={`${activeItem.afterImg} 600w, ${activeItem.afterImg} 1200w`} sizes="(max-width: 768px) 100vw, 800px" loading="lazy" alt={`After transformation of ${activeItem.name}`} className="w-full h-full object-cover" onLoad={onImageLoad} />
        <div className="absolute top-6 right-6 z-20">
          <Badge className="bg-black/80 backdrop-blur-md border-primary/40 text-primary px-3 py-1 text-sm font-default font-bold uppercase tracking-wider">
            After
          </Badge>
        </div>
      </div>

      {}
      <div className="absolute inset-0 w-full h-full overflow-hidden border-r border-primary/60" style={{
      width: `${sliderPosition}%`
    }}>
        <img src={activeItem.beforeImg} srcSet={`${activeItem.beforeImg} 600w, ${activeItem.beforeImg} 1200w`} sizes="(max-width: 768px) 100vw, 800px" loading="lazy" alt={`Before transformation of ${activeItem.name}`} className="absolute top-0 left-0 max-w-none h-full object-cover" onLoad={onImageLoad} style={{
        width: containerRef.current ? containerRef.current.offsetWidth : '100%'
      }} />
        <div className="absolute top-6 left-6 z-20">
          <Badge className="bg-black/80 backdrop-blur-md border-white/10 text-white/70 px-3 py-1 text-sm font-default font-bold uppercase tracking-wider">
            Before
          </Badge>
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-40" />
      </div>

      {}
      <div className="absolute top-0 bottom-0 w-0.5 bg-primary cursor-ew-resize z-30 shadow-[0_0_15px_rgba(203,176,129,0.5)] flex items-center justify-center" style={{
      left: `${sliderPosition}%`
    }}>
        <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center shadow-2xl border-2 border-background transform transition-transform hover:scale-110 active:scale-95">
          <MoveHorizontal className="w-5 h-5 text-background" />
        </div>
      </div>
      
      {}
      <div className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none transition-opacity duration-500 ${isDragging || sliderPosition !== 50 ? 'opacity-0' : 'opacity-100'}`}>
        <div className="bg-black/60 backdrop-blur-md text-white p-3 rounded-full flex items-center justify-center border border-primary/30">
          <MoveHorizontal className="w-5 h-5 text-primary" />
        </div>
      </div>
    </div>;
});
SliderDisplay.displayName = "SliderDisplay";
export default function RealResultsRealPeople() {
  const [activeId, setActiveId] = useState(1);
  const [sliderPosition, setSliderPosition] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const [loadedCount, setLoadedCount] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const rafRef = useRef<number | null>(null);
  const lastX = useRef<number>(0);
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll();
  const activeItem = useMemo(() => transformations.find(t => t.id === activeId) || transformations[0], [activeId]);
  const handleMouseDown = useCallback(() => setIsDragging(true), []);
  const handleMouseUp = useCallback(() => setIsDragging(false), []);
  const handleImageLoad = useCallback(() => {
    setLoadedCount(prev => prev + 1);
  }, []);
  const handleMove = useCallback((clientX: number) => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const x = clientX - rect.left;
      const percentage = Math.min(Math.max(x / rect.width * 100, 0), 100);
      setSliderPosition(percentage);
    }
  }, []);
  const throttledMove = useCallback((clientX: number) => {
    lastX.current = clientX;
    if (rafRef.current !== null) return;
    rafRef.current = requestAnimationFrame(() => {
      handleMove(lastX.current);
      rafRef.current = null;
    });
  }, [handleMove]);
  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (isDragging) throttledMove(e.clientX);
  }, [isDragging, throttledMove]);
  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    throttledMove(e.touches[0].clientX);
  }, [throttledMove]);
  useEffect(() => {
    setLoadedCount(0);
    setSliderPosition(50);
  }, [activeId]);
  useEffect(() => {
    if (loadedCount === 2) {
      setSliderPosition(50);
    }
  }, [loadedCount]);
  useEffect(() => {
    const stopDragging = () => setIsDragging(false);
    window.addEventListener("mouseup", stopDragging);
    return () => {
      window.removeEventListener("mouseup", stopDragging);
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
  }, []);
  return <section data-section-id="54714" id="the-transformation" ref={sectionRef} className={`dark bg-background text-foreground py-20 relative overflow-hidden transition-opacity duration-700 ${isVisible ? "opacity-100" : "opacity-0"}`}>
      <div className="absolute inset-0 bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1630148/img/concrete_texture_dark.png)] opacity-10 bg-cover bg-center pointer-events-none mix-blend-overlay" />
      
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/5 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-primary/10 rounded-full blur-[120px] pointer-events-none" />

      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center mb-16 max-w-3xl mx-auto">
          <Badge variant="outline" className="mb-4 border-primary/30 text-primary uppercase tracking-widest font-default">
            The Results
          </Badge>
          <h2 className="text-4xl md:text-5xl font-default font-black uppercase tracking-tight mb-6">See The&nbsp;<span className="text-primary">Difference</span></h2>
          <p className="text-muted-foreground text-lg font-default">From faded paint to a gloss finish, our results speak for themselves.</p>
        </div>

        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 xl:col-span-8">
            <SliderDisplay activeItem={activeItem} sliderPosition={sliderPosition} isDragging={isDragging} containerRef={containerRef} handleMouseDown={handleMouseDown} handleMouseMove={handleMouseMove} handleTouchMove={handleTouchMove} handleMove={handleMove} onImageLoad={handleImageLoad} loadedCount={loadedCount} />
            <p className="text-center text-xs uppercase tracking-widest text-muted-foreground/60 mt-4 font-default">slide to see results</p>
          </div>

          <div className="lg:col-span-5 xl:col-span-4 flex flex-col h-full justify-center">
            <div className="bg-card/50 border border-primary/10 rounded-2xl p-8 shadow-2xl relative overflow-hidden backdrop-blur-sm">
              <div className="absolute top-0 right-0 w-24 h-24 bg-primary/5 rounded-bl-full -mr-4 -mt-4" />
              
              <div className="relative z-10 flex flex-col">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-2xl font-default font-bold text-white tracking-tight">{activeItem.name}</h3>
                    <p className="text-primary font-default font-medium tracking-wide">{activeItem.duration}</p>
                  </div>
                  <div className="h-12 w-12 rounded-full bg-secondary flex items-center justify-center border border-primary/20">
                    <Scale className="w-6 h-6 text-primary" />
                  </div>
                </div>

                <div className="hidden md:block space-y-4 mb-8">
                  <h4 className="text-xl font-serif italic text-primary/90">"{activeItem.title}"</h4>
                  <p className="text-muted-foreground leading-relaxed font-default">"{activeItem.quote}"</p>
                </div>

                <div className="space-y-3 order-first md:order-none mb-6 md:mb-0">
                  <p className="text-xs font-default font-semibold text-white/40 uppercase tracking-widest mb-2">Select Project</p>
                  <div className="grid grid-cols-3 gap-3">
                    {transformations.map((item, i) => <Button key={item.id} data-index={i} onClick={() => setActiveId(item.id)} variant="ghost" className={`p-0 h-auto w-full relative rounded-lg overflow-hidden aspect-square border-2 transition-all duration-300 group hover:bg-transparent ${activeId === item.id ? "border-primary ring-4 ring-primary/10 scale-105 z-10" : "border-transparent opacity-40 hover:opacity-100 hover:border-primary/20"}`}>
                        <img src={item.afterImg} alt={item.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                        {activeId === item.id && <div className="absolute inset-0 bg-primary/10 mix-blend-overlay" />}
                      </Button>)}
                  </div>
                </div>

                <div className="mt-8 pt-6 border-t border-primary/10">
                  <Link to="https://app.urable.com/virtual-shop/RwwACfmUUCbP7DfYZWTj" className="w-full">
                    <Button className="w-full h-12 text-base font-default font-bold uppercase tracking-widest bg-primary text-background hover:bg-primary/90 transition-colors" size="lg">
                      Book Your Detail <ArrowRight className="ml-2 w-5 h-5" />
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}